import { section } from 'framer-motion/client';
import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'



// Function to check discount eligibility via API
const checkDiscountEligibility = async (organizationName, attendeesCount) => {
  if (!organizationName) {
    return {
      isEligible: false,
      discountAmount: 0,
      hotelDiscountCode: '',
      organizationExists: false
    };
  }
  
  try {
    const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002';
    console.log('Using API URL:', apiUrl);
    const response = await fetch(`${apiUrl}/api/register/check-discount`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ companyName: organizationName, attendeesCount }), // Server still expects companyName
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Error checking discount eligibility:', result.message);
      return { isEligible: false, discountAmount: 0, hotelDiscountCode: '', organizationExists: false };
    }
    
    return result.data;
  } catch (error) {
    console.error('Error checking discount eligibility:', error);
    return { isEligible: false, discountAmount: 0, hotelDiscountCode: '', organizationExists: false };
  }
};

const Register = () => {
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002'
  
  // ---- Resume registration via resumeToken (hash parameter) ----
  const getResumeToken = () => {
    // 1. Check normal query string first
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('resumeToken')) return searchParams.get('resumeToken');
    if (searchParams.get('token')) return searchParams.get('token');

    // 2. Fallback: check hash fragment (for HashRouter links)
    const hash = window.location.hash || '';
    const match = hash.match(/resumeToken=([^&]+)/) || hash.match(/token=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  useEffect(() => {
    const resumeToken = getResumeToken();
    if (!resumeToken) return;

    (async () => {
      try {
        const response = await fetch(`${apiUrl}/api/register/resume?token=${resumeToken}`);
        const result = await response.json();
        if (!response.ok || !result?.data) {
          console.error('Failed to fetch resume registration:', result.message);
          return;
        }
        const reg = result.data;
        // Pre-fill form data with the registration details
        setFormData(prev => ({
          ...prev,
          ...reg,
          attendeesCount: reg.attendeesCount?.toString() || '1',
          companyName: reg.companyName || reg.organization || '',
          organization: reg.organization || reg.companyName || ''
        }));

        // Build submittedData to open confirmation modal directly
        const formattedPrice = new Intl.NumberFormat('en-ET', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(reg.finalPrice || 0);

        setSubmittedData({
          ...reg,
          registrationId: reg._id,
          packageName: reg.paymentOption === '3day' ? '3-Day Full Access' : '2-Day Access',
          packagePrice: formattedPrice,
          discountApplied: reg.discountApplied,
          discountAmount: reg.discountAmount,
          hotelDiscountCode: reg.hotelDiscountCode,
          checkoutUrl: '',
          txRef: reg.txRef || ''
        });
        setShowConfirmation(true);
        console.log('Registration resumed for', reg.email);
      } catch (err) {
        console.error('Error resuming registration:', err);
      }
    })();
  }, []);

  // State for group discount
  const [attendeesCount, setAttendeesCount] = useState(1)
  
  // State for discount code
  const [discountCode, setDiscountCode] = useState('')
  const [discountError, setDiscountError] = useState('')
  const [discountApplied, setDiscountApplied] = useState(false)
  const [fixedPriceOverride, setFixedPriceOverride] = useState(null)
  const [discountCurrency, setDiscountCurrency] = useState('USD')
  const [discountCodeValidated, setDiscountCodeValidated] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [hotelDiscountCode, setHotelDiscountCode] = useState('')

  // Function to validate discount code
  const validateDiscountCode = async (code) => {
    if (!code) {
      setDiscountError('Please enter a discount code');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/discount-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const result = await response.json();

      if (!response.ok) {
        setDiscountError(result.message || 'Invalid discount code');
      setDiscountCodeValidated(false);
        setDiscountApplied(false);
        setFixedPriceOverride(null);
        return;
      }

      setDiscountError('');
      setDiscountApplied(true);
      setFixedPriceOverride(result.data.fixedPrice);
      setDiscountCurrency(result.data.currency);
      setDiscountCodeValidated(true);
      setHotelDiscountCode(result.data.code);
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountError('Failed to validate discount code. Please try again.');
      setDiscountCodeValidated(false);
      setDiscountApplied(false);
      setFixedPriceOverride(null);
    }
  };

  // Function to validate and apply fixed-price discount code
  const applyDiscountCode = async () => {
    if (!discountCode) {
      setDiscountError('Please enter a discount code');
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode })
      });
      const result = await response.json();
      if (!response.ok) {
        setDiscountError(result.message || 'Invalid or inactive discount code');
        setDiscountCodeValidated(false);
        setFixedPriceOverride(null);
        return;
      }
      setFixedPriceOverride(result.data.fixedPrice);
      setDiscountError('');
    } catch (err) {
      setDiscountError('Unable to validate discount code. Please try again later.');
      setDiscountCodeValidated(false);
      setFixedPriceOverride(null);
    }
  };
  
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    companyName: '', // Added for group discount
    attendeesCount: '1', // Added for group discount
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    referenceNumber: '',
    phoneNumber: '',
    website: '',
    officeNumber: '',
    requireVisa: '',
    // Visa assistance fields
    nationality: '',
    passportNumber: '',
    passportExpiryDate: '',
    passportIssueDate: '',
    departingFrom: '',
    departingCountry: '',
    paymentOption: '3day' // Default to 3-day full access
  })

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    }
    
    setFormData(newFormData)
  }
  
  // State to track if the organization is new or existing
  const [isNewOrganization, setIsNewOrganization] = useState(true);
  const [showGroupDiscountInfo, setShowGroupDiscountInfo] = useState(false);

  // Function to check organization status and apply discount if applicable
  const checkAndApplyDiscount = async (organizationName, attendeesCount) => {
    console.log(`checkAndApplyDiscount called with: ${organizationName}, ${attendeesCount}`);
    
    // Reset discount state first
    setDiscountApplied(false);
    setDiscountAmount(0);
    setHotelDiscountCode('');
    setShowGroupDiscountInfo(false);
    setIsNewOrganization(true); // Default to new organization
    
    if (!organizationName || organizationName.length < 3) {
      console.log('Organization name too short, skipping eligibility check');
      return;
    }
    
    try {
      console.log(`Sending API request to check eligibility for: ${organizationName}`);
      
      // Check eligibility via API - use organization name
      const response = await checkDiscountEligibility(organizationName, attendeesCount);
      console.log('API response:', response);
      
      const { isEligible, discountAmount, hotelDiscountCode, organizationExists } = response;
      
      // Always set company name for consistency
      setFormData(prev => ({
        ...prev,
        companyName: organizationName,
        organization: organizationName
      }));
      
      console.log(`Organization exists: ${organizationExists}, isEligible: ${isEligible}`);
      
      if (organizationExists) {
        // If organization already exists in database, apply discount but DON'T show group discount info
        console.log('Organization exists in database, applying discount without showing group info');
        setIsNewOrganization(false);
        setShowGroupDiscountInfo(false); // Explicitly hide group discount info
        
        // Always apply discount for existing organizations
        setDiscountApplied(true);
        setDiscountAmount(discountAmount);
        setHotelDiscountCode(hotelDiscountCode);
        
        // Force UI update
        setTimeout(() => {
          console.log('Forced UI update for existing organization');
        }, 0);
      } else {
        // New organization - show group discount information section
        console.log('New organization, showing group discount information');
        setIsNewOrganization(true);
        setShowGroupDiscountInfo(true);
        
        // Apply discount if eligible (5+ attendees)
        if (isEligible) {
          console.log(`New organization is eligible for discount: ${discountAmount}%`);
          setDiscountApplied(true);
          setDiscountAmount(discountAmount);
          setHotelDiscountCode(hotelDiscountCode);
        } else {
          console.log('New organization not eligible for discount yet');
        }
      }
    } catch (error) {
      console.error('Error checking discount eligibility:', error);
    }
  }

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [submittedData, setSubmittedData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    setEmailError(null)
    
    // Validate visa assistance selection
    if (!formData.requireVisa) {
      setError('Please select whether you require visa assistance or not')
      setIsSubmitting(false)
      return
    }
    
    // Make sure to check organization eligibility one more time before submitting
    if (formData.organization && formData.organization.length >= 3) {
      console.log('Final eligibility check before submission');
      await checkAndApplyDiscount(formData.organization, formData.attendeesCount);
    }
    
    try {
      // Log discount status for debugging
      console.log(`Submitting form with discount status: ${discountApplied ? 'APPLIED' : 'NOT APPLIED'}`);
      console.log(`Discount amount: ${discountAmount}%`);
      console.log(`Hotel discount code: ${hotelDiscountCode || 'none'}`);
      
      // Apply discount if it was determined to be eligible by the API
      // This will work for both new organizations with 2+ attendees and
      // existing eligible organizations regardless of current attendees count
      const shouldApplyDiscount = discountApplied;
      
      // Calculate base and final prices
      const basePrice = formData.paymentOption === '3day' ? 1500 : 1000;
      let finalPrice = shouldApplyDiscount ? basePrice * 0.9 : basePrice;
// Override with fixed-price discount if applied
if (fixedPriceOverride !== null) {
  finalPrice = fixedPriceOverride;
}
      
      // Send data to backend API
      const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002';
      console.log('Using API URL for registration:', apiUrl);
      const response = await fetch(`${apiUrl}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          discountApplied: shouldApplyDiscount,
          discountAmount: shouldApplyDiscount ? discountAmount : 0,
          hotelDiscountCode: shouldApplyDiscount ? hotelDiscountCode : '',
          finalPrice,
          discountCode: discountCodeValidated ? discountCode.trim().toUpperCase() : ''
        }),
      })
      
      const result = await response.json()
      
      if (!response.ok) {
        // Handle duplicate email error gracefully
        if (result?.errorType === 'EMAIL_EXISTS') {
          setEmailError('This email is already registered. Please use a different email.');
          // Smooth scroll to the email input so the user sees the error
          const emailInput = document.getElementById('email');
          if (emailInput) {
            emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emailInput.focus();
          }
        }
        throw new Error(result.message || 'Registration failed')
      }
      
      console.log('Registration successful:', result)
      
      // Format the price for display
      const formattedPrice = new Intl.NumberFormat('en-ET', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(finalPrice);
      
      setSubmittedData({
        ...formData,
        registrationId: result.data._id,
        packageName: formData.paymentOption === '3day' ? '3-Day Full Access' : '2-Day Access',
        packagePrice: formattedPrice,
        discountApplied: shouldApplyDiscount,
        discountAmount: shouldApplyDiscount ? discountAmount : 0,
        hotelDiscountCode: shouldApplyDiscount ? hotelDiscountCode : '',
        checkoutUrl: result.data.checkoutUrl || 'https://checkout.chapa.co/checkout/payment/AfrMarConf' + result.data._id
      })
      
      // Show confirmation message
      setShowConfirmation(true)
            // Initialize payment with Chapa
      try {
        const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002';
        console.log('Using API URL for payment initialization:', apiUrl);
        
        // Generate a unique transaction reference
        const txRef = `AMC-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        
        // Create absolute URLs for payment callbacks
        let frontendBaseUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin;
        
        // Ensure the URL has a protocol
        if (!frontendBaseUrl.startsWith('http')) {
          frontendBaseUrl = window.location.protocol + '//' + frontendBaseUrl;
        }
        
        // Remove trailing slash if exists
        frontendBaseUrl = frontendBaseUrl.endsWith('/') ? frontendBaseUrl.slice(0, -1) : frontendBaseUrl;
        
        // Create a unique timestamp to prevent caching
        const timestamp = Date.now();
        
        // Build the success URL with hash-based routing
        const successUrl = new URL(`${frontendBaseUrl}/#/TravelBookings`);
        successUrl.search = new URLSearchParams({
          tx_ref: txRef,
          registration_id: result.data._id,
          status: 'success',
          _: timestamp // Cache buster
        }).toString();
        
        // Build the cancel URL
        const cancelUrl = new URL(`${frontendBaseUrl}/#/register`);
        cancelUrl.search = new URLSearchParams({
          status: 'cancelled',
          _: timestamp // Cache buster
        }).toString();
        
        // Convert to string and ensure proper encoding
        const successUrlStr = successUrl.toString();
        const cancelUrlStr = cancelUrl.toString();
        
        // Log the final URLs for debugging
        console.log('Success URL:', successUrlStr);
        console.log('Cancel URL:', cancelUrlStr);
        
        console.log('Payment callback URLs:', { successUrl, cancelUrl });
        
        // Function to format phone number for Chapa
        const formatPhoneNumber = (phone) => {
          if (!phone) return '+251900000000'; // Default Ethiopian number if none provided
          
          // Remove all non-digit characters
          let digits = phone.replace(/\D/g, '');
          
          // If it starts with 0 (local format), replace with 251
          if (digits.startsWith('0')) {
            digits = `251${digits.substring(1)}`;
          } 
          // If it's a local number without country code, add 251
          else if (digits.length === 9) {
            digits = `251${digits}`;
          }
          // If it's an international number with country code, ensure it starts with +
          else if (digits.length >= 10 && !digits.startsWith('251')) {
            return `+${digits}`;
          }
          
          // Ensure the number starts with + and has the country code
          return `+${digits.startsWith('251') ? digits : `251${digits}`}`;
        };
        
        // Format the phone number
        const formattedPhone = formatPhoneNumber(formData.phoneNumber);
        console.log('Formatted phone number:', formattedPhone);
        
        // Prepare payment data
        const paymentData = {
          registrationId: result.data._id,
          amount: finalPrice.toString(),
          email: formData.email,
          firstName: formData.firstName || 'Customer',
          lastName: formData.lastName || 'Name',
          phone: formattedPhone,
          packageName: formData.paymentOption === '3day' ? '3-Day Full Access' : '2-Day Access',
          // Use the properly formatted URLs
          callback_url: successUrlStr,
          return_url: successUrlStr,
          tx_ref: txRef, // Pass the transaction reference to the backend
          discountCode: discountCode || undefined
        };
        
        console.log('Sending payment data to backend:', paymentData);
        
        const paymentResponse = await fetch(`${apiUrl}/api/payment/initialize`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(paymentData)
        });
        console.log('Payment initialization response status:', paymentResponse.status);
        const paymentResult = await paymentResponse.json();
        console.log('Payment initialization response:', paymentResult);
        
        if (!paymentResponse.ok) {
          console.error('Payment initialization failed:', paymentResult);
          throw new Error(paymentResult.message || 'Failed to initialize payment');
        }
        
        // Store the transaction reference in localStorage for payment verification
        localStorage.setItem('amts_txRef', txRef);
        localStorage.setItem('amts_registrationId', result.data._id);
        console.log('Stored txRef in localStorage:', txRef);
        
        // If we have a checkout URL, show the confirmation modal first then auto-redirect
        if (paymentResult.data) {
          // Try different possible URL fields in the response
          const checkoutUrl = paymentResult.data.checkout_url || 
                              paymentResult.data.url || 
                              paymentResult.data.checkoutUrl;
          
          if (checkoutUrl) {
            console.log('Got checkout URL:', checkoutUrl);
            // Save details so the modal can display them
            setSubmittedData(prev => ({
              ...prev,
              checkoutUrl,
              txRef: txRef
            }));
            setShowConfirmation(true);

            return; // Show confirmation modal and wait for user action
          }
        }
        
        // If no direct checkout URL, update the UI with the payment link
        setSubmittedData(prev => ({
          ...prev,
          checkoutUrl: paymentResult.data?.checkout_url || paymentResult.data?.checkoutUrl,
          txRef: txRef
        }));
      } catch (paymentErr) {
        console.error('Error initializing payment:', paymentErr)
      }
    } catch (err) {
      console.error('Error submitting form:', err)
      setError(err.message || 'An error occurred during registration. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper component for required field indicator
  const RequiredIndicator = () => (
    <span className="required-indicator">*</span>
  )

  // Check for payment success from URL params when component mounts
  useEffect(() => {
    // Check if the URL contains payment success parameters from Chapa
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status') || urlParams.get('transaction_status');
    const txRef = urlParams.get('tx_ref') || urlParams.get('trx_ref');
    const registrationId = urlParams.get('registration_id') || (txRef ? txRef.split('-').pop() : null);
    
    console.log('URL Parameters:', { status, txRef, registrationId, search: window.location.search });
    
    // If payment was successful, verify the payment before showing confirmation
    const verifyPayment = async () => {
      if ((status === 'success' || status === 'completed') && txRef) {
        try {
          console.log('Verifying payment with reference:', txRef);
          const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002';
          
          // First try direct verification with the tx_ref
          const response = await fetch(`${apiUrl}/api/payment/verify?tx_ref=${txRef}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include' // Include cookies for session handling
          });
          
          const result = await response.json();
          
          if (response.ok && (result.success || result.status === 'success')) {
            console.log('Payment verification successful:', result);
            setPaymentCompleted(true);
            
            // Show the travel bookings modal after a short delay
            setTimeout(() => {
              setShowTravelBookings(true);
              document.body.style.overflow = 'hidden';
            }, 1000);
            
            // Clear the URL parameters after successful verification
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error('Payment verification failed:', result?.message || 'Unknown error');
            // If verification fails, still show success if we have a success status
            if (status === 'success' || status === 'completed') {
              console.log('Showing success based on status parameter');
              setPaymentCompleted(true);
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          // If there's an error but we have success status, still show success
          if (status === 'success' || status === 'completed') {
            console.log('Showing success based on status parameter after error');
            setPaymentCompleted(true);
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
    };
    
    verifyPayment();
    // Cleanup function to re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [window.location.search]); // Include window.location.search in dependencies

  // Confirmation modal component
  const ConfirmationModal = ({ data, onClose }) => {
    if (!data) return null;
    
    const modalOverlayStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    };
    
    const modalContainerStyle = {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'auto'
    };
    
    const modalHeaderStyle = {
      padding: '1.25rem 1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
      color: 'white',
      borderRadius: '0.5rem 0.5rem 0 0'
    };
    
    const modalContentStyle = {
      padding: '1.5rem'
    };
    
    const packageCardStyle = {
      backgroundColor: '#f0f7ff',
      border: '1px solid #bfdbfe',
      borderRadius: '0.5rem',
      padding: '1.25rem',
      marginBottom: '1.5rem'
    };
    
    const discountBadgeStyle = {
      backgroundColor: '#10B981',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.875rem'
    };
    
    const hotelDiscountSectionStyle = {
      marginTop: '1rem',
      padding: '0.75rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem'
    };
    
    const hotelDiscountCodeStyle = {
      backgroundColor: 'white',
      padding: '0.5rem',
      border: '1px solid #3b82f6',
      borderRadius: '0.25rem',
      textAlign: 'center',
      marginTop: '0.5rem'
    };
    
    const paymentButtonStyle = {
      backgroundColor: '#10B981',
      color: 'white',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.375rem',
      fontWeight: 'bold',
      fontSize: '1.125rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      cursor: 'pointer',
      width: '100%',
      marginBottom: '0.75rem'
    };
    
    const closeButtonStyle = {
      backgroundColor: '#F3F4F6',
      color: '#4B5563',
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      border: '1px solid #D1D5DB',
      cursor: 'pointer',
      width: '100%'
    };
    
    return (
      <div style={modalOverlayStyle}>
        <div style={modalContainerStyle}>
          <div style={modalHeaderStyle}>
            <h2 style={{margin: 0, fontSize: '1.25rem', fontWeight: 600}}>Registration Successful!</h2>
            <button style={{background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer'}} onClick={onClose}>×</button>
          </div>
          <div style={modalContentStyle}>
            <p style={{marginBottom: '1rem'}}>Thank you for registering, {data.firstName} {data.lastName}!</p>
            
            <div style={{margin: '1.5rem 0'}}>
              <h3 style={{fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: '1.25rem 0 0.75rem'}}>Selected Package:</h3>
              <div style={packageCardStyle}>
                <div style={{fontWeight: 600, fontSize: '1.125rem', color: '#1e40af', marginBottom: '0.5rem'}}>{data.packageName}</div>
                <div style={{fontWeight: 700, fontSize: '1.5rem', color: '#1e3a8a'}}>{data.packagePrice}</div>
                {data.discountApplied && (
                  <div style={{marginTop: '0.5rem'}}>
                    <span style={discountBadgeStyle}>
                      {data.discountAmount}% Group Discount Applied
                    </span>
                  </div>
                )}
              </div>
              
              {data.hotelDiscountCode && (
                <div style={hotelDiscountSectionStyle}>
                  <h4 style={{fontWeight: 'bold', color: '#1e40af', margin: '0 0 0.5rem 0'}}>Your Hotel Discount Code:</h4>
                  <div style={hotelDiscountCodeStyle}>
                    <code style={{fontSize: '1.125rem', fontWeight: 'bold', color: '#f97316'}}>{data.hotelDiscountCode}</code>
                  </div>
                  <p style={{fontSize: '0.875rem', marginTop: '0.5rem'}}>Use this code when booking your hotel to receive a special discount.</p>
                </div>
              )}
              
              <h3 style={{fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: '1.25rem 0 0.75rem'}}>Next Steps:</h3>
              <ol style={{paddingLeft: '1.5rem', marginBottom: '1.5rem'}}>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>Click the <strong>"Proceed to Payment"</strong> button below to complete your payment</li>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>You will be redirected to our secure payment gateway (Chapa)</li>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>After successful payment, you will receive a confirmation email at <strong>{data.email}</strong></li>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>Your event access credentials will be sent to you after payment confirmation</li>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>
                  You'll also receive information about <strong>discounted hotel and flight bookings</strong>
                </li>
                <li style={{marginBottom: '0.75rem', lineHeight: 1.5, color: '#4b5563'}}>
                  <strong>Note:</strong> after completing your payment, please wait <strong>about 10&nbsp;seconds</strong> while we verify the transaction. You’ll then be redirected automatically to your booking summary in the mean time you can download your receipt.
                </li>
              </ol>
            </div>
            
            <div style={{marginTop: '2rem'}}>
              <button 
                style={paymentButtonStyle}
                onClick={() => {
                  if (data.checkoutUrl) {
                    // Store the transaction reference in localStorage before redirecting
                    localStorage.setItem('amts_txRef', data.txRef);
                    console.log('Stored txRef in localStorage:', data.txRef);
                    // Redirect to Chapa payment gateway
                    window.location.href = data.checkoutUrl;
                  } else {
                    alert('Payment system is currently unavailable. Please try again later.');
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" style={{height: '1.5rem', width: '1.5rem', marginRight: '0.5rem'}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                Proceed to Payment
              </button>
              <button 
                style={closeButtonStyle}
                onClick={onClose}
              >
                Close this window
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  // Check URL for payment success and process it
  useEffect(() => {
    const processSuccessfulPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const txRef = urlParams.get('tx_ref');
      const registrationId = urlParams.get('registration_id');
      
      console.log('Checking URL parameters on component render:', { 
        status, 
        txRef, 
        registrationId,
        url: window.location.href 
      });
      
      // Only process if we have a success status and a transaction reference
      if (status === 'success' && txRef && registrationId) {
        try {
          console.log('Processing successful payment...');
          
          // Show loading state
          setIsSubmitting(true);
          
          // Call the payment success endpoint to update registration and send confirmation email
          const apiUrl = import.meta.env.PROD ? 'https://africa-maritime-talent.onrender.com' : 'http://localhost:5002';
          const paymentResponse = await fetch(`${apiUrl}/api/register/payment/success`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              registrationId,
              txRef: txRef.toString() // Ensure txRef is a string
            }),
          });
          
          if (!paymentResponse.ok) {
            throw new Error(`HTTP error! status: ${paymentResponse.status}`);
          }
          
          const paymentData = await paymentResponse.json();
          console.log('Payment success response:', paymentData);
          
          if (paymentData.success) {
            // Store registration data in localStorage as a fallback
            localStorage.setItem('amts_registration', JSON.stringify(paymentData.registration));
            
            // Redirect to thank you page with registration data
            navigate('/thank-you', { 
              state: { 
                registration: paymentData.registration,
                paymentSuccess: true 
              },
              replace: true // Replace current history entry
            });
            
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error('Payment processing failed:', paymentData.message);
            setError(paymentData.message || 'Payment was successful but there was an error processing your registration. Please contact support with your registration ID.');
          }
        } catch (error) {
          console.error('Error processing payment:', error);
          setError(`There was an error processing your payment: ${error.message}. Please contact support with your registration ID.`);
        } finally {
          setIsSubmitting(false);
        }
      }
    };
    
    processSuccessfulPayment();
  }, [navigate]);
  
  // Render the main component
  return (
    <section className="bg-white opacity-[95%]" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
      {/* Payment Success Banner */}
      {paymentCompleted && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <div className="max-w-7xl mx-auto flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Payment successful! Your registration is complete. You will be redirected to the travel and accommodation page shortly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Dark background banner for navbar visibility */}
      <div className="w-full h-20 bg-gradient-to-r from-primary-navy to-primary-green"></div>
      
      {/* Minimal space for navbar */}
      <div className="w-full h-22 bg-gradient-to-r from-primary-navy to-primary-green"></div>
      
      <div className="registration-container pt-2">
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        
        {showConfirmation && (
          <ConfirmationModal 
            data={submittedData} 
            onClose={() => setShowConfirmation(false)} 
          />
        )}

        <div className="registration-card">
        {/* Header */}
        <div className="registration-header bg-gradient-to-r from-primary-navy to-primary-blue text-white p-6 rounded-t-lg">
          <h1 className="text-2xl font-bold">Registration</h1>
        </div>
        
        <form onSubmit={handleSubmit} className="registration-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Personal Information</h2>
            <div className="form-grid form-grid-3">
              <div className="form-group">
                <label htmlFor="title" className="form-label">Title</label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  placeholder="Mr/ Ms /Dr....."
                  value={formData.title}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name <RequiredIndicator /></label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name <RequiredIndicator /></label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
                        <div className="form-grid form-grid-2 mt-6">
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email <RequiredIndicator /></label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                />
                {emailError && (
                  <p className="form-error text-red-600" style={{ color: 'red' }}>{emailError}</p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="organization" className="form-label">Organization</label>
                <input
                  type="text"
                  name="organization"
                  id="organization"
                  value={formData.organization}
                  onChange={(e) => {
                    handleChange(e);
                    // Check company eligibility when organization name changes
                    if (e.target.value.length >= 3) {
                      console.log(`Checking organization: ${e.target.value}`);
                      // Use a small delay to avoid too many API calls while typing
                      clearTimeout(window.organizationCheckTimer);
                      window.organizationCheckTimer = setTimeout(() => {
                        checkAndApplyDiscount(e.target.value, formData.attendeesCount);
                      }, 500);
                    } else {
                      // Reset group discount info when organization field is cleared
                      setShowGroupDiscountInfo(false);
                    }
                  }}
                  onBlur={(e) => {
                    // Check again on blur to ensure we have the latest status
                    if (e.target.value.length >= 3) {
                      checkAndApplyDiscount(e.target.value, formData.attendeesCount);
                    }
                  }}
                  className="form-input"
                />
                <p className="form-hint">Please let us know which organization you represent.</p>
              </div>
            </div>
            
            {/* Group Discount Information - Only shown for new organizations */}
            {isNewOrganization && showGroupDiscountInfo && (
              <div className="form-group mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-primary-navy mb-2">Group Discount Available!</h3>
                <p className="mb-3">Your organization is eligible for a 10% group discount when registering 5 or more attendees.</p>
                
                <div className="form-group">
                  <label htmlFor="attendeesCount" className="form-label">Number of Attendees from your Organization</label>
                  <select
                    name="attendeesCount"
                    id="attendeesCount"
                    value={formData.attendeesCount}
                    onChange={(e) => {
                      handleChange(e);
                      // Re-check discount eligibility when attendees count changes
                      checkAndApplyDiscount(formData.organization, e.target.value);
                    }}
                    className="form-input"
                  >
                    <option value="4">4 (No discount)</option>
                    <option value="5">5 (10% discount)</option>
                    <option value="6">6 (10% discount)</option>
                    <option value="7">7 (10% discount)</option>
                    <option value="8">8 (10% discount)</option>
                  </select>
                </div>
              </div>
            )}
            
            {/* Show discount applied message for both new and existing organizations */}
            {discountApplied && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-700">
                  <span className="font-semibold">Discount Applied!</span> Your organization qualifies for a 10% discount.
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Hotel Discount Code: <span className="font-mono font-semibold">{hotelDiscountCode}</span>
                </p>
                {!isNewOrganization && (
                  <p className="text-sm text-blue-600 mt-1">
                    Your organization has been registered before. The group discount has been automatically applied.
                  </p>
                )}
              </div>
            )}
          </div>
          


{/* Address Information */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Address Information</h2>
            <p className="section-description">
              The address information you provide will be used for verifying your identity and personalizing your experience on our platform. 
              We ensure the confidentiality and security of your address details in compliance with our privacy policy.
            </p>
            
            <div className="mb-4">
              <h3 className="text-md font-medium text-gray-700 mb-3">Address of the Company</h3>
            </div>
            
            <div className="form-grid form-grid-1">
              <div className="form-group">
                <label htmlFor="addressLine1" className="form-label">Address Line 1 <RequiredIndicator /></label>
                <input
                  type="text"
                  name="addressLine1"
                  id="addressLine1"
                  required
                  value={formData.addressLine1}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="addressLine2" className="form-label">Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  id="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="landmark" className="form-label">Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  id="landmark"
                  value={formData.landmark}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label htmlFor="city" className="form-label">City <RequiredIndicator /></label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="state" className="form-label">State/Province</label>
                  <input
                    type="text"
                    name="state"
                    id="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="zip" className="form-label">Postal/Zip Code</label>
                  <input
                    type="text"
                    name="zip"
                    id="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="country" className="form-label">Country <RequiredIndicator /></label>
                <select
                  name="country"
                  id="country"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select a country</option>
                  <option value="Afghanistan">Afghanistan</option>
                  <option value="Albania">Albania</option>
                  <option value="Algeria">Algeria</option>
                  <option value="Andorra">Andorra</option>
                  <option value="Angola">Angola</option>
                  <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Armenia">Armenia</option>
                  <option value="Australia">Australia</option>
                  <option value="Austria">Austria</option>
                  <option value="Azerbaijan">Azerbaijan</option>
                  <option value="Bahamas">Bahamas</option>
                  <option value="Bahrain">Bahrain</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="Barbados">Barbados</option>
                  <option value="Belarus">Belarus</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Belize">Belize</option>
                  <option value="Benin">Benin</option>
                  <option value="Bhutan">Bhutan</option>
                  <option value="Bolivia">Bolivia</option>
                  <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                  <option value="Botswana">Botswana</option>
                  <option value="Brazil">Brazil</option>
                  <option value="Brunei">Brunei</option>
                  <option value="Bulgaria">Bulgaria</option>
                  <option value="Burkina Faso">Burkina Faso</option>
                  <option value="Burundi">Burundi</option>
                  <option value="Cabo Verde">Cabo Verde</option>
                  <option value="Cambodia">Cambodia</option>
                  <option value="Cameroon">Cameroon</option>
                  <option value="Canada">Canada</option>
                  <option value="Central African Republic">Central African Republic</option>
                  <option value="Chad">Chad</option>
                  <option value="Chile">Chile</option>
                  <option value="China">China</option>
                  <option value="Colombia">Colombia</option>
                  <option value="Comoros">Comoros</option>
                  <option value="Congo">Congo</option>
                  <option value="Costa Rica">Costa Rica</option>
                  <option value="Cote d'Ivoire">Cote d'Ivoire</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Cuba">Cuba</option>
                  <option value="Cyprus">Cyprus</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Djibouti">Djibouti</option>
                  <option value="Dominica">Dominica</option>
                  <option value="Dominican Republic">Dominican Republic</option>
                  <option value="Ecuador">Ecuador</option>
                  <option value="Egypt">Egypt</option>
                  <option value="El Salvador">El Salvador</option>
                  <option value="Equatorial Guinea">Equatorial Guinea</option>
                  <option value="Eritrea">Eritrea</option>
                  <option value="Estonia">Estonia</option>
                  <option value="Eswatini">Eswatini</option>
                  <option value="Ethiopia">Ethiopia</option>
                  <option value="Fiji">Fiji</option>
                  <option value="Finland">Finland</option>
                  <option value="France">France</option>
                  <option value="Gabon">Gabon</option>
                  <option value="Gambia">Gambia</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Germany">Germany</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Greece">Greece</option>
                  <option value="Grenada">Grenada</option>
                  <option value="Guatemala">Guatemala</option>
                  <option value="Guinea">Guinea</option>
                  <option value="Guinea-Bissau">Guinea-Bissau</option>
                  <option value="Guyana">Guyana</option>
                  <option value="Haiti">Haiti</option>
                  <option value="Honduras">Honduras</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Iceland">Iceland</option>
                  <option value="India">India</option>
                  <option value="Indonesia">Indonesia</option>
                  <option value="Iran">Iran</option>
                  <option value="Iraq">Iraq</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Israel">Israel</option>
                  <option value="Italy">Italy</option>
                  <option value="Jamaica">Jamaica</option>
                  <option value="Japan">Japan</option>
                  <option value="Jordan">Jordan</option>
                  <option value="Kazakhstan">Kazakhstan</option>
                  <option value="Kenya">Kenya</option>
                  <option value="Kiribati">Kiribati</option>
                  <option value="Kuwait">Kuwait</option>
                  <option value="Kyrgyzstan">Kyrgyzstan</option>
                  <option value="Laos">Laos</option>
                  <option value="Latvia">Latvia</option>
                  <option value="Lebanon">Lebanon</option>
                  <option value="Lesotho">Lesotho</option>
                  <option value="Liberia">Liberia</option>
                  <option value="Libya">Libya</option>
                  <option value="Liechtenstein">Liechtenstein</option>
                  <option value="Lithuania">Lithuania</option>
                  <option value="Luxembourg">Luxembourg</option>
                  <option value="Madagascar">Madagascar</option>
                  <option value="Malawi">Malawi</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Maldives">Maldives</option>
                  <option value="Mali">Mali</option>
                  <option value="Malta">Malta</option>
                  <option value="Marshall Islands">Marshall Islands</option>
                  <option value="Mauritania">Mauritania</option>
                  <option value="Mauritius">Mauritius</option>
                  <option value="Mexico">Mexico</option>
                  <option value="Micronesia">Micronesia</option>
                  <option value="Moldova">Moldova</option>
                  <option value="Monaco">Monaco</option>
                  <option value="Mongolia">Mongolia</option>
                  <option value="Montenegro">Montenegro</option>
                  <option value="Morocco">Morocco</option>
                  <option value="Mozambique">Mozambique</option>
                  <option value="Myanmar">Myanmar</option>
                  <option value="Namibia">Namibia</option>
                  <option value="Nauru">Nauru</option>
                  <option value="Nepal">Nepal</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Nicaragua">Nicaragua</option>
                  <option value="Niger">Niger</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="North Korea">North Korea</option>
                  <option value="North Macedonia">North Macedonia</option>
                  <option value="Norway">Norway</option>
                  <option value="Oman">Oman</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Palau">Palau</option>
                  <option value="Palestine">Palestine</option>
                  <option value="Panama">Panama</option>
                  <option value="Papua New Guinea">Papua New Guinea</option>
                  <option value="Paraguay">Paraguay</option>
                  <option value="Peru">Peru</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Poland">Poland</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Romania">Romania</option>
                  <option value="Russia">Russia</option>
                  <option value="Rwanda">Rwanda</option>
                  <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                  <option value="Saint Lucia">Saint Lucia</option>
                  <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                  <option value="Samoa">Samoa</option>
                  <option value="San Marino">San Marino</option>
                  <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Senegal">Senegal</option>
                  <option value="Serbia">Serbia</option>
                  <option value="Seychelles">Seychelles</option>
                  <option value="Sierra Leone">Sierra Leone</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Solomon Islands">Solomon Islands</option>
                  <option value="Somalia">Somalia</option>
                  <option value="South Africa">South Africa</option>
                  <option value="South Korea">South Korea</option>
                  <option value="South Sudan">South Sudan</option>
                  <option value="Spain">Spain</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="Sudan">Sudan</option>
                  <option value="Suriname">Suriname</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Syria">Syria</option>
                  <option value="Taiwan">Taiwan</option>
                  <option value="Tajikistan">Tajikistan</option>
                  <option value="Tanzania">Tanzania</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Timor-Leste">Timor-Leste</option>
                  <option value="Togo">Togo</option>
                  <option value="Tonga">Tonga</option>
                  <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                  <option value="Tunisia">Tunisia</option>
                  <option value="Turkey">Turkey</option>
                  <option value="Turkmenistan">Turkmenistan</option>
                  <option value="Tuvalu">Tuvalu</option>
                  <option value="Uganda">Uganda</option>
                  <option value="Ukraine">Ukraine</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United States">United States</option>
                  <option value="Uruguay">Uruguay</option>
                  <option value="Uzbekistan">Uzbekistan</option>
                  <option value="Vanuatu">Vanuatu</option>
                  <option value="Vatican City">Vatican City</option>
                  <option value="Venezuela">Venezuela</option>
                  <option value="Vietnam">Vietnam</option>
                  <option value="Yemen">Yemen</option>
                  <option value="Zambia">Zambia</option>
                  <option value="Zimbabwe">Zimbabwe</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Contact Information</h2>
            <p className="section-description">
              Your contact information will be used for account-related communications and to keep you informed about updates and important notifications. 
              We value your privacy and will not share your contact details with third parties without your consent.
            </p>
            
            <div className="form-grid form-grid-3">
              <div className="form-group">
                <label htmlFor="phoneNumber" className="form-label">Phone Number <RequiredIndicator /></label>
                <input
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="+251911223344"
                  pattern="^\+?[0-9\s-()]{10,15}$"
                  title="Please enter a valid phone number (e.g., +251911223344 or 0911223344)"
                  inputMode="tel"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="website" className="form-label">Website</label>
                <input
                  type="text"
                  name="website"
                  id="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="officeNumber" className="form-label">Office Number</label>
                <input
                  type="tel"
                  name="officeNumber"
                  id="officeNumber"
                  value={formData.officeNumber}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
          
          {/* Additional Information */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Additional Information</h2>
            
            <div className="form-grid form-grid-1">
              <div className="radio-group">
                <label className="form-label">Do you require visa assistance? <RequiredIndicator /></label>
                <div className="radio-options">
                  <div className="radio-option">
                    <input
                      id="visa-yes"
                      name="requireVisa"
                      type="radio"
                      value="yes"
                      checked={formData.requireVisa === 'yes'}
                      onChange={handleChange}
                      className="radio-input"
                      required
                    />
                    <label htmlFor="visa-yes" className="radio-label">Yes</label>
                  </div>
                  <div className="radio-option">
                    <input
                      id="visa-no"
                      name="requireVisa"
                      type="radio"
                      value="no"
                      checked={formData.requireVisa === 'no'}
                      onChange={handleChange}
                      className="radio-input"
                    />
                    <label htmlFor="visa-no" className="radio-label">No</label>
                  </div>
                </div>
              </div>
              
              {formData.requireVisa === 'yes' && (
                <div className="visa-assistance-form">
                  <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Visa Assistance</h2>
                  <p className="visa-form-description">Please provide the following information to help us with your visa application process.</p>
                  
                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label htmlFor="nationality" className="form-label">Nationality <RequiredIndicator /></label>
                      <select
                        name="nationality"
                        id="nationality"
                        required={formData.requireVisa === 'yes'}
                        value={formData.nationality}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">--Select Country--</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Botswana">Botswana</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Brunei">Brunei</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cabo Verde">Cabo Verde</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Canada">Canada</option>
                        <option value="Central African Republic">Central African Republic</option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo">Congo</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominica">Dominica</option>
                        <option value="Dominican Republic">Dominican Republic</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Eswatini">Eswatini</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Greece">Greece</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guinea-Bissau">Guinea-Bissau</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kiribati">Kiribati</option>
                        <option value="Korea, North">Korea, North</option>
                        <option value="Korea, South">Korea, South</option>
                        <option value="Kosovo">Kosovo</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Laos">Laos</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libya">Libya</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Marshall Islands">Marshall Islands</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Micronesia">Micronesia</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar">Myanmar</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nauru">Nauru</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Palau">Palau</option>
                        <option value="Palestine">Palestine</option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">Papua New Guinea</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                        <option value="Saint Lucia">Saint Lucia</option>
                        <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                        <option value="Samoa">Samoa</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Seychelles">Seychelles</option>
                        <option value="Sierra Leone">Sierra Leone</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Solomon Islands">Solomon Islands</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="South Sudan">South Sudan</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syria">Syria</option>
                        <option value="Taiwan">Taiwan</option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania">Tanzania</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Timor-Leste">Timor-Leste</option>
                        <option value="Togo">Togo</option>
                        <option value="Tonga">Tonga</option>
                        <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Tuvalu">Tuvalu</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Vanuatu">Vanuatu</option>
                        <option value="Vatican City">Vatican City</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="passportNumber" className="form-label">Passport Number <RequiredIndicator /></label>
                      <input
                        type="text"
                        name="passportNumber"
                        id="passportNumber"
                        required={formData.requireVisa === 'yes'}
                        value={formData.passportNumber}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label htmlFor="passportExpiryDate" className="form-label">Passport Expiry Date <RequiredIndicator /></label>
                      <input
                        type="date"
                        name="passportExpiryDate"
                        id="passportExpiryDate"
                        required={formData.requireVisa === 'yes'}
                        value={formData.passportExpiryDate}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Please enter your date of expire for your passport."
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="passportIssueDate" className="form-label">Date of Passport Issue <RequiredIndicator /></label>
                      <input
                        type="date"
                        name="passportIssueDate"
                        id="passportIssueDate"
                        required={formData.requireVisa === 'yes'}
                        value={formData.passportIssueDate}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="departingFrom" className="form-label">Departing From (City) <RequiredIndicator /></label>
                    <input
                      type="text"
                      name="departingFrom"
                      id="departingFrom"
                      required={formData.requireVisa === 'yes'}
                      value={formData.departingFrom}
                      onChange={handleChange}
                      className="form-input"
                    />
                    <p className="form-hint">City</p>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="departingCountry" className="form-label">Country <RequiredIndicator /></label>
                    <select
                      name="departingCountry"
                      id="departingCountry"
                      required={formData.requireVisa === 'yes'}
                      value={formData.departingCountry}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">--Select Country--</option>
                      <option value="Afghanistan">Afghanistan</option>
                      <option value="Albania">Albania</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Andorra">Andorra</option>
                      <option value="Angola">Angola</option>
                      <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                      <option value="Argentina">Argentina</option>
                      <option value="Armenia">Armenia</option>
                      <option value="Australia">Australia</option>
                      <option value="Austria">Austria</option>
                      <option value="Azerbaijan">Azerbaijan</option>
                      <option value="Bahamas">Bahamas</option>
                      <option value="Bahrain">Bahrain</option>
                      <option value="Bangladesh">Bangladesh</option>
                      <option value="Barbados">Barbados</option>
                      <option value="Belarus">Belarus</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Belize">Belize</option>
                      <option value="Benin">Benin</option>
                      <option value="Bhutan">Bhutan</option>
                      <option value="Bolivia">Bolivia</option>
                      <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                      <option value="Botswana">Botswana</option>
                      <option value="Brazil">Brazil</option>
                      <option value="Brunei">Brunei</option>
                      <option value="Bulgaria">Bulgaria</option>
                      <option value="Burkina Faso">Burkina Faso</option>
                      <option value="Burundi">Burundi</option>
                      <option value="Cabo Verde">Cabo Verde</option>
                      <option value="Cambodia">Cambodia</option>
                      <option value="Cameroon">Cameroon</option>
                      <option value="Canada">Canada</option>
                      <option value="Central African Republic">Central African Republic</option>
                      <option value="Chad">Chad</option>
                      <option value="Chile">Chile</option>
                      <option value="China">China</option>
                      <option value="Colombia">Colombia</option>
                      <option value="Comoros">Comoros</option>
                      <option value="Congo">Congo</option>
                      <option value="Costa Rica">Costa Rica</option>
                      <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                      <option value="Croatia">Croatia</option>
                      <option value="Cuba">Cuba</option>
                      <option value="Cyprus">Cyprus</option>
                      <option value="Czech Republic">Czech Republic</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Djibouti">Djibouti</option>
                      <option value="Dominica">Dominica</option>
                      <option value="Dominican Republic">Dominican Republic</option>
                      <option value="Ecuador">Ecuador</option>
                      <option value="Egypt">Egypt</option>
                      <option value="El Salvador">El Salvador</option>
                      <option value="Equatorial Guinea">Equatorial Guinea</option>
                      <option value="Eritrea">Eritrea</option>
                      <option value="Estonia">Estonia</option>
                      <option value="Eswatini">Eswatini</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Fiji">Fiji</option>
                      <option value="Finland">Finland</option>
                      <option value="France">France</option>
                      <option value="Gabon">Gabon</option>
                      <option value="Gambia">Gambia</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Germany">Germany</option>
                      <option value="Ghana">Ghana</option>
                      <option value="Greece">Greece</option>
                      <option value="Grenada">Grenada</option>
                      <option value="Guatemala">Guatemala</option>
                      <option value="Guinea">Guinea</option>
                      <option value="Guinea-Bissau">Guinea-Bissau</option>
                      <option value="Guyana">Guyana</option>
                      <option value="Haiti">Haiti</option>
                      <option value="Honduras">Honduras</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Iceland">Iceland</option>
                      <option value="India">India</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Iran">Iran</option>
                      <option value="Iraq">Iraq</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Israel">Israel</option>
                      <option value="Italy">Italy</option>
                      <option value="Jamaica">Jamaica</option>
                      <option value="Japan">Japan</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kazakhstan">Kazakhstan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Kiribati">Kiribati</option>
                      <option value="Korea, North">Korea, North</option>
                      <option value="Korea, South">Korea, South</option>
                      <option value="Kosovo">Kosovo</option>
                      <option value="Kuwait">Kuwait</option>
                      <option value="Kyrgyzstan">Kyrgyzstan</option>
                      <option value="Laos">Laos</option>
                      <option value="Latvia">Latvia</option>
                      <option value="Lebanon">Lebanon</option>
                      <option value="Lesotho">Lesotho</option>
                      <option value="Liberia">Liberia</option>
                      <option value="Libya">Libya</option>
                      <option value="Liechtenstein">Liechtenstein</option>
                      <option value="Lithuania">Lithuania</option>
                      <option value="Luxembourg">Luxembourg</option>
                      <option value="Madagascar">Madagascar</option>
                      <option value="Malawi">Malawi</option>
                      <option value="Malaysia">Malaysia</option>
                      <option value="Maldives">Maldives</option>
                      <option value="Mali">Mali</option>
                      <option value="Malta">Malta</option>
                      <option value="Marshall Islands">Marshall Islands</option>
                      <option value="Mauritania">Mauritania</option>
                      <option value="Mauritius">Mauritius</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Micronesia">Micronesia</option>
                      <option value="Moldova">Moldova</option>
                      <option value="Monaco">Monaco</option>
                      <option value="Mongolia">Mongolia</option>
                      <option value="Montenegro">Montenegro</option>
                      <option value="Morocco">Morocco</option>
                      <option value="Mozambique">Mozambique</option>
                      <option value="Myanmar">Myanmar</option>
                      <option value="Namibia">Namibia</option>
                      <option value="Nauru">Nauru</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Nicaragua">Nicaragua</option>
                      <option value="Niger">Niger</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="North Macedonia">North Macedonia</option>
                      <option value="Norway">Norway</option>
                      <option value="Oman">Oman</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Palau">Palau</option>
                      <option value="Palestine">Palestine</option>
                      <option value="Panama">Panama</option>
                      <option value="Papua New Guinea">Papua New Guinea</option>
                      <option value="Paraguay">Paraguay</option>
                      <option value="Peru">Peru</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Poland">Poland</option>
                      <option value="Portugal">Portugal</option>
                      <option value="Qatar">Qatar</option>
                      <option value="Romania">Romania</option>
                      <option value="Russia">Russia</option>
                      <option value="Rwanda">Rwanda</option>
                      <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                      <option value="Saint Lucia">Saint Lucia</option>
                      <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                      <option value="Samoa">Samoa</option>
                      <option value="San Marino">San Marino</option>
                      <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Senegal">Senegal</option>
                      <option value="Serbia">Serbia</option>
                      <option value="Seychelles">Seychelles</option>
                      <option value="Sierra Leone">Sierra Leone</option>
                      <option value="Singapore">Singapore</option>
                      <option value="Slovakia">Slovakia</option>
                      <option value="Slovenia">Slovenia</option>
                      <option value="Solomon Islands">Solomon Islands</option>
                      <option value="Somalia">Somalia</option>
                      <option value="South Africa">South Africa</option>
                      <option value="South Sudan">South Sudan</option>
                      <option value="Spain">Spain</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Sudan">Sudan</option>
                      <option value="Suriname">Suriname</option>
                      <option value="Sweden">Sweden</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Syria">Syria</option>
                      <option value="Taiwan">Taiwan</option>
                      <option value="Tajikistan">Tajikistan</option>
                      <option value="Tanzania">Tanzania</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Timor-Leste">Timor-Leste</option>
                      <option value="Togo">Togo</option>
                      <option value="Tonga">Tonga</option>
                      <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                      <option value="Tunisia">Tunisia</option>
                      <option value="Turkey">Turkey</option>
                      <option value="Turkmenistan">Turkmenistan</option>
                      <option value="Tuvalu">Tuvalu</option>
                      <option value="Uganda">Uganda</option>
                      <option value="Ukraine">Ukraine</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Uruguay">Uruguay</option>
                      <option value="Uzbekistan">Uzbekistan</option>
                      <option value="Vanuatu">Vanuatu</option>
                      <option value="Vatican City">Vatican City</option>
                      <option value="Venezuela">Venezuela</option>
                      <option value="Vietnam">Vietnam</option>
                      <option value="Yemen">Yemen</option>
                      <option value="Zambia">Zambia</option>
                      <option value="Zimbabwe">Zimbabwe</option>
                    </select>
                    <p className="form-hint">Country</p>
                  </div>
                  
                  <div className="form-group">
                    <p className="visa-note">Note: 10%+ discounts available for multiple attendees from the same company.</p>
                  </div>
                </div>
              )}
              
              
              
              {/* Group Discount Section - Only shown for new organizations */}
              {isNewOrganization && (
                <div className="form-section-inner mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-primary-navy mb-3">Group Discount Information</h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Companies sending multiple attendees qualify for a 10% discount. Please provide the following information:
                  </p>
                
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label htmlFor="companyName" className="form-label">Company Name <RequiredIndicator /></label>
                    <input
                      type="text"
                      name="companyName"
                      id="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                    <p className="form-hint">Enter the exact company name for group discount eligibility.</p>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="attendeesCount" className="form-label">How many people from your company will attend? <RequiredIndicator /></label>
                    <input
                      type="number"
                      name="attendeesCount"
                      id="attendeesCount"
                      min="1"
                      value={formData.attendeesCount}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                    <p className="form-hint">Groups of 2 or more qualify for a 10% discount.</p>
                  </div>
                </div>
                
                {formData.attendeesCount >= 5 && discountApplied && (
                  <div className="discount-alert mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-green mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-primary-green">
                        {discountAmount}% Group Discount will be applied to your registration!
                      </span>
                    </div>
                    {hotelDiscountCode && (
                      <p className="mt-2 text-sm text-gray-700">
                        You'll also receive a hotel discount code after registration, use this code when booking your hotel stay: <strong>{hotelDiscountCode}</strong>
                      </p>
                    )}
                  </div>
                )}
                
                {formData.attendeesCount < 5 && (
                  <div className="info-alert mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-blue-700">
                        Enter 5 or more attendees to qualify for a 10% group discount.
                        If the number of actual attendees from your company is less than stated you may be charged the full price
                      </span>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
          
          {/* Payment Options */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Registration Package</h2>
            <p className="section-description">
               Payment will be collected after registration is complete.
            </p>
            
            <div className="form-grid form-grid-1">
              <div className="radio-group">
                <label className="form-label"> <RequiredIndicator /></label>
                <div className="radio-options payment-options">
                  <div className="payment-option">
                    <input
                      id="payment-3day"
                      name="paymentOption"
                      type="radio"
                      value="3day"
                      checked={formData.paymentOption === '3day'}
                      onChange={handleChange}
                      className="radio-input"
                    />
                    <label htmlFor="payment-3day" className="payment-label">
                      <div className="payment-content">
                        <div className="payment-title text-primary-navy font-bold">3-Day Full Access</div>
                        <div className="payment-price text-primary-orange font-bold">
                          {fixedPriceOverride !== null ? (
                              <span>
                                <span className="line-through text-gray-500 mr-2">$1,500 USD</span>
                                <span>{fixedPriceOverride} USD</span>
                                <span className="ml-2 bg-primary-green text-white text-xs px-2 py-1 rounded-full">Fixed Price</span>
                              </span>
                            ) : discountApplied ? (
                              <span>
                                <span className="line-through text-gray-500 mr-2">$1,500 USD</span>
                                <span>$1,350 USD</span>
                                <span className="ml-2 bg-primary-green text-white text-xs px-2 py-1 rounded-full">10% OFF</span>
                              </span>
                            ) : (
                              <span>$1,500 USD</span>
                            )}
                        </div>
                        <div className="payment-description">Complete access to all conference sessions, workshops, and networking events for all 3 days</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="info-card">
                <h3 className="info-title">Payment Information</h3>
                <p className="info-text">
                  <strong>Payment Methods:</strong> Credit card, bank transfer, and mobile payment options will be available after registration.
                </p>
                <p className="info-text">
                  <strong>Cancellation Policy:</strong> Full refund available up to 30 days before the event. 50% refund available up to 14 days before the event.
                </p>
              </div>
            </div>
          </div>
          
          {/* Quote Reference Number Section */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Quote Reference Number (Optional)</h2>
            <p className="section-description">If you received a quote, enter the reference number here so we can link your registration.</p>
            <div className="form-grid form-grid-1">
              <div className="form-group">
                <label htmlFor="referenceNumber" className="form-label">Reference Number</label>
                <input
                  type="text"
                  id="referenceNumber"
                  name="referenceNumber"
                  value={formData.referenceNumber}
                  onChange={handleChange}
                  placeholder="e.g. AMC-202506261234"
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Discount Code Section */}
          <div className="form-section">
            <h2 className="section-title text-primary-navy font-bold text-xl border-b-2 border-[#D4572E] pb-2 mb-4">Discount Code</h2>
            <p className="section-description">
              Enter a discount code if you have one. This will override any other discounts.
            </p>

            <div className="form-grid form-grid-1">
              <div className="form-group">
                <label htmlFor="discountCode" className="form-label">Discount Code</label>
                <div className="relative">
                  <input
                    type="text"
                    name="discountCode"
                    id="discountCode"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setDiscountError('');
                      setDiscountCodeValidated(false);
                    }}
                    placeholder="Enter discount code"
                    className={`form-input ${discountError ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => validateDiscountCode(discountCode)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary-navy text-white px-3 py-1 rounded-md hover:bg-primary-blue transition-colors"
                  >
                    Apply
                  </button>
                </div>
                {discountError && (
                  <p className="mt-1 text-sm text-red-500">{discountError}</p>
                )}
                {discountCodeValidated && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-green-700">
                        Discount code applied! Your price will be {fixedPriceOverride} {discountCurrency}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="submit-container">
            <button
              type="submit"
              className="submit-button bg-gradient-to-r from-primary-navy to-primary-blue hover:from-primary-blue hover:to-primary-navy text-white font-bold py-3 px-6 rounded-md shadow-md transition-all duration-300 flex items-center justify-center gap-2"
              disabled={isSubmitting || (discountCode && !discountCodeValidated)}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Register
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </section>
  )
};

export default Register;